import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';

const steps = [
  {
    number: '01',
    title: 'CHOOSE A FORMULA',
    description: 'Select the package that matches your needs. Each formula is designed for a specific outcome.'
  },
  {
    number: '02',
    title: 'COMPLETE CHECKOUT',
    description: 'Secure payment. Instant confirmation. Your project is officially in the queue.'
  },
  {
    number: '03',
    title: 'FILL THE INTAKE',
    description: 'Brief intake form captures everything we need. No back-and-forth. No delays.'
  },
  {
    number: '04',
    title: 'RECEIVE DELIVERABLES',
    description: 'The formula runs. You receive structured deliverables on schedule.'
  }
];

// Smooth, professional easing
const smoothEase = [0.4, 0, 0.2, 1];

const SceneProcess = () => {
  const sectionRef = useRef(null);
  
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"]
  });
  
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);
  const lineProgress = useTransform(scrollYProgress, [0.25, 0.75], ["0%", "100%"]);

  return (
    <section 
      ref={sectionRef}
      className="scene relative min-h-screen flex items-center justify-center py-32 overflow-hidden"
      id="process"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-background" />
      
      <motion.div 
        className="relative z-10 w-full max-w-4xl mx-auto px-6"
        style={{ opacity }}
      >
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: smoothEase }}
          viewport={{ once: true }}
          className="text-center mb-20"
        >
          <p className="font-body text-sm tracking-[0.3em] text-accent/80 uppercase mb-5">
            THE PROCESS
          </p>
          <h2 className="font-display text-hero text-foreground">
            HOW IT WORKS
          </h2>
        </motion.div>
        
        {/* Process steps */}
        <div className="relative">
          {/* Vertical progress line */}
          <div className="absolute left-8 md:left-12 top-0 bottom-0 w-px bg-border/50">
            <motion.div 
              className="w-full bg-accent/60 origin-top"
              style={{ height: lineProgress }}
            />
          </div>
          
          {/* Steps */}
          <div className="space-y-14">
            {steps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ 
                  duration: 0.7, 
                  delay: index * 0.08,
                  ease: smoothEase
                }}
                viewport={{ once: true, margin: "-80px" }}
                className="process-step"
              >
                {/* Number */}
                <div className="relative z-10 w-16 md:w-24 flex-shrink-0">
                  <span className="process-number">{step.number}</span>
                  {/* Dot on line */}
                  <div className="absolute left-8 md:left-12 top-4 w-2.5 h-2.5 bg-accent/80 rounded-full -translate-x-1/2" />
                </div>
                
                {/* Content */}
                <div className="flex-grow pt-2">
                  <h3 className="font-display text-2xl md:text-3xl text-foreground mb-3">
                    {step.title}
                  </h3>
                  <p className="font-body text-base text-muted-foreground/80 max-w-md leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
        
        {/* Bottom note - Quiet confidence */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.4, ease: smoothEase }}
          viewport={{ once: true }}
          className="text-center mt-24"
        >
          <div className="gold-line w-12 mx-auto mb-6" />
          <p className="font-body text-sm text-muted-foreground/60 tracking-wider">
            No meetings. No scope creep. Just results.
          </p>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default SceneProcess;
